import type {AggregationSectorReference} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {BaseModel} from "crqlar-react-common/dist/models/common/base.model";
import {Limit} from "crqlar-react-common/dist/models/common/limit";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import type {FontSource} from "../../common/newFontPicker";
import {
    DigitalSigningCornerStyle,
    DigitalSigningVariant,
    TipBase,
    TipFormat,
    TipUnit
} from "../public/digitalSigning.types";

/*
    The organization's signing templates (crqlar-api-v3 features/digital-signing.md,
    "Templates and tip defaults"): each one where it is offered - its sectors, every folio in them -,
    the page's design, its title and thank-you, the legal links of its footer and the tip.
    A link is issued with one of them; created from the backend's starters, deleting archives.
*/

/** Settings › Billing › Digital signing; a template opens under its id. */
export const DIGITAL_SIGNING_TEMPLATES_PATH = '/system/digitalSigning';

/** `?create=1` opens the list with its create dialog. */
export const CREATE_TEMPLATE_PARAM = 'create';

/** What a starter is for, and the layout of a snapshot's page. */
export type DigitalSigningTemplateKind = 'SPA' | 'RESTAURANT';

/**
 * The kind of the starter a sector's type has, as the backend's kinds name their sector type: a spa or
 * restaurant sector no template covers gets its first one from the link dialog, other types have none.
 */
export const STARTER_KIND_BY_SECTOR_TYPE: Partial<Record<SectorType, DigitalSigningTemplateKind>> = {
    [SectorType.SPA]: 'SPA',
    [SectorType.RESTAURANT]: 'RESTAURANT',
};

/** Tip configuration for a signing template. */
export interface DigitalSigningTipSettings {
    enabled: boolean
    format: TipFormat
    /** SMART: what the lower tier is in. Absent in older responses: FIXED. */
    smartLowerUnit?: TipUnit
    /** SMART: what the upper tier is in. Absent in older responses: PERCENT. */
    smartUpperUnit?: TipUnit
    /** FIXED, and SMART's lower tier in FIXED. */
    fixedPresetsCents: number[]
    /** SMART's upper tier in FIXED. Absent in older responses: copies of [fixedPresetsCents]. */
    upperFixedPresetsCents?: number[]
    /** PERCENT, and SMART's upper tier in PERCENT. */
    percentPresets: number[]
    /** SMART's lower tier in PERCENT. Absent in older responses: copies of [percentPresets]. */
    lowerPercentPresets?: number[]
    /** SMART: a base below it gets the lower tier, anything else the upper one. */
    smartThresholdCents: number
    base: TipBase
    /** Percentage amounts are rounded to this step: 1 (to the cent), 10, 50 or 100. */
    roundingCents: number
    /** Outlined on the page, never selected. */
    highlightedPresetIndex?: number | null
    /** Only where `preselectAllowed`. */
    preselectedPresetIndex?: number | null
    /**
     * Staff's name of the tip by page language (de, en), the page's heading instead of ours - always
     * followed by our "· optional". Sent in full with every save (only the non-blank ones, trimmed);
     * older responses leave it out.
     */
    labels?: Record<string, string>
    /** A line under the heading by page language; like [labels], none without one. */
    descriptions?: Record<string, string>
}

/** Design settings for signing page appearance. */
export interface DigitalSigningDesign {
    variant: DigitalSigningVariant
    primaryColor?: string | null
    panelColor?: string | null
    logoMedia?: Media | null
    /** The photo of Hero and Background, cut to 16:9 on upload. */
    heroMedia?: Media | null
    /** Background: how strongly the photo shows through the veil, in percent (10-90); absent: Medium. */
    heroOpacity?: number | null
    cornerStyle: DigitalSigningCornerStyle
    /** Saved with the font, so the page loads it the way it was added. Standard keeps no link and no family. */
    fontSource: FontSource
    /** Google Fonts: the stylesheet; own font: the uploaded file. */
    fontUrl?: string | null
    fontFamily?: string | null
}

// The font fields blank where the backend leaves them out, as a change of the font source leaves them: switching
// back to a saved Standard then leaves the template unchanged
export const DEFAULT_DESIGN: DigitalSigningDesign = {variant: 'PAPER', cornerStyle: 'ROUNDED', fontSource: 'STANDARD', fontUrl: '', fontFamily: ''};

/** Background's image opacities: the label's translation key and the percentage saved. */
export const HERO_OPACITY_PRESETS = [
    {label: 'Subtle', percent: 25},
    {label: 'Medium', percent: 45},
    {label: 'Strong', percent: 65},
] as const;

/** Signing template model. */
export interface DigitalSigningTemplate extends BaseModel {
    name?: string
    scopedSectorIds: string[]
    tipSettings: DigitalSigningTipSettings
    design?: Partial<DigitalSigningDesign>
    privacyPolicyUrl?: string
    imprintUrl?: string
    thankYouMessages?: Record<string, string>
    titles?: Record<string, string>
    subtitles?: Record<string, string>
    preselectAllowed: boolean
    currency: string
}

/** Save request DTO for creating or updating a signing template. */
export interface DigitalSigningTemplateSaveRequest {
    /** update only. */
    id?: string
    /** Trimmed, at most 80 characters. */
    name: string
    /** The organization's own sectors; an empty list offers the template nowhere. */
    scopedSectorIds: string[]
    /** Always with both of SMART's units and all four lists, whichever the format shows. */
    tipSettings: DigitalSigningTipSettings
        & Required<Pick<DigitalSigningTipSettings, 'smartLowerUnit' | 'smartUpperUnit' | 'upperFixedPresetsCents' | 'lowerPercentPresets'>>
    design: DigitalSigningDesign
    /** "" clears a link; left out it would keep the stored one. */
    privacyPolicyUrl: string
    imprintUrl: string
    /** Replaces the stored messages: only the non-blank ones, trimmed; {} clears them all. */
    thankYouMessages: Record<string, string>
    /** Like [thankYouMessages]. */
    titles: Record<string, string>
    subtitles: Record<string, string>
}

/** A starter of the create dialog's picker, labeled in one language, with the draft it would create. */
export interface DigitalSigningTemplateStarter {
    /** For templates/instantiate. */
    id: string
    kind: DigitalSigningTemplateKind
    name: string
    description: string
    /** Scoped to every sector of its kind, with the country's tips. */
    draft: DigitalSigningTemplate
}

export interface DigitalSigningTemplateInstantiateRequest {
    templateId: string
    /** Picks the name the created template gets. */
    language?: string
    /** Overrides the starter's own name. */
    name?: string
    /** Overrides the starter's scope, every sector of its kind; an empty list offers it nowhere. */
    scopedSectorIds?: string[]
}

/** A row of the list page, from the aggregation route. */
export interface DigitalSigningTemplateListRow {
    id: string
    name?: string
    /** Its sectors that still exist, in the template's order; empty: offered nowhere. */
    sectors: AggregationSectorReference[]
    insertDate?: Date
    updateDate?: Date
}

/** The fields the aggregation route sorts the list by; it refuses any other. Unsorted: by name. */
export type DigitalSigningTemplateListSortField = 'name' | 'updateDate' | 'id';

export interface DigitalSigningTemplateListRequest {
    /** Part of the name, case-insensitive. */
    search?: string
    /** At most 100 a page. */
    limit: Limit
}

/** Three suggestions, and the guest's own amount as the fourth slot. */
export const TIP_SUGGESTIONS = 3;

/** The rounding steps the backend accepts, in cents; 1 is exact. */
export const TIP_ROUNDING_STEPS = [1, 10, 50, 100] as const;

/** The page languages staff can word the page's texts in - the backend takes these keys only. */
export const PAGE_LANGUAGES = ['de', 'en'] as const;

export type PageLanguage = typeof PAGE_LANGUAGES[number];

/** At most what the backend keeps of a template's name, trimmed. */
export const TEMPLATE_NAME_MAX_LENGTH = 80;

/** At most what the backend keeps of a thank-you, trimmed. */
export const THANK_YOU_MAX_LENGTH = 200;

/** At most what the backend keeps of the page's title and of its subtitle, trimmed. */
export const TITLE_MAX_LENGTH = 60;
export const SUBTITLE_MAX_LENGTH = 120;

/** At most what the backend keeps of the tip's name and of its description, trimmed. */
export const TIP_LABEL_MAX_LENGTH = 40;
export const TIP_DESCRIPTION_MAX_LENGTH = 200;

/**
 * A sector's tip services as confirm picks them, for Who gets the tip: the default one - for staff without
 * their own - and each staff member's own (the tip service that has them as its linked resource). They
 * are set on the services, so every template offered in the sector shows the same.
 */
export interface DigitalSigningTipServices {
    sectorId: string
    /** Null: tips of staff without their own go on as a plain line without a service or VAT. */
    defaultServiceId?: string | null
    /** By name. */
    staff: DigitalSigningTipStaff[]
}

export interface DigitalSigningTipStaff {
    resourceId: string
    name: string
    /** Null: the default one. */
    tipServiceId?: string | null
}
